import { CopyableText, DataGrid, Dialog } from '@ijoin/components';
import {
  useGetAdminManagedAccountProviderAdvisoryOrganizations,
  useGetManagedAccountProviderById,
  useGetManagedAccountProviderPortfolios
} from '@ijoin/hooks';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Breadcrumbs,
  Card,
  CardContent,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { createFileRoute, Link } from '@tanstack/react-router';

import { camelCase } from 'lodash';
import { useState } from 'react';

import { LinkButton } from '../../components';

type PortfolioFundDetail = {
  cusip: string;
  fundId: string;
  investorProfileDescription: string;
  isin: string;
  rateOfReturnProbability: number;
  targetedRateOfReturn: null | number;
  volatilityDescription: string;
  worstCaseGainLoss: number;
};

const ManagedAccountProviderDetailPage = () => {
  const { managedAccountProviderId } = Route.useParams();
  const provider = useGetManagedAccountProviderById(managedAccountProviderId);
  const advisoryOrgs = useGetAdminManagedAccountProviderAdvisoryOrganizations(
    managedAccountProviderId
  );
  const portfolios = useGetManagedAccountProviderPortfolios(
    managedAccountProviderId
  );

  const [viewingPortfolio, setViewingPortfolio] =
    useState<null | PortfolioFundDetail>(null);

  const onCloseViewingPortfolio = () => setViewingPortfolio(null);

  const advisoryOrgColumns: GridColDef[] = [
    {
      field: 'name',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.name)}
          params={{ advisoryOrganizationId: params.row.advisoryOrganizationId }}
          to='/organizations/$advisoryOrganizationId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      field: 'city',
      flex: 1,
      headerName: 'City'
    },
    {
      field: 'state',
      flex: 0.75,
      headerName: 'State'
    }
  ];

  const portfolioColumns: GridColDef[] = [
    {
      field: 'fundId',
      flex: 1.5,
      headerName: 'Fund ID'
    },
    {
      field: 'cusip',
      flex: 0.75,
      headerName: 'CUSIP'
    },
    {
      field: 'volatilityDescription',
      flex: 1,
      headerName: 'Volatility'
    },
    {
      field: 'investorProfileDescription',
      flex: 1,
      headerName: 'Investor Profile'
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <IconButton
          data-testid={camelCase(`view ${params.row.fundId}`)}
          onClick={() =>
            setViewingPortfolio({
              cusip: params.row.cusip ?? '',
              fundId: params.row.fundId,
              investorProfileDescription: params.row.investorProfileDescription,
              isin: params.row.isin ?? '',
              rateOfReturnProbability: params.row.rateOfReturnProbability,
              targetedRateOfReturn: params.row.targetedRateOfReturn,
              volatilityDescription: params.row.volatilityDescription,
              worstCaseGainLoss: params.row.worstCaseGainLoss
            })
          }>
          <InfoOutlinedIcon />
        </IconButton>
      ),
      sortable: false,
      width: 65
    }
  ];

  const methodology = provider.data?.ManagedAccountMethodologies;

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Breadcrumbs>
        <Link
          data-component='link'
          data-testid='breadcrumbMethodologies'
          to='/methodologies'>
          Methodologies
        </Link>
        {methodology && (
          <Link
            data-component='link'
            data-testid='breadcrumbMethodology'
            params={{
              managedAccountMethodologyId:
                methodology.managedAccountMethodologyId
            }}
            to='/methodologies/$managedAccountMethodologyId'>
            {methodology.managedAccountMethodologyName}
          </Link>
        )}
        <Typography color='text.primary' data-testid='breadcrumbProvider'>
          {provider.data?.description ?? '...'}
        </Typography>
      </Breadcrumbs>

      {provider.data && (
        <Card sx={{ p: 1 }}>
          <CardContent>
            <Stack spacing={3}>
              <Stack
                direction='row'
                spacing={3}
                sx={{ flexWrap: 'wrap', gap: 2 }}>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Description
                  </Typography>
                  <Typography variant='h5'>
                    {provider.data.description}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Methodology
                  </Typography>
                  <Typography variant='subtitle1'>
                    {methodology?.managedAccountMethodologyName}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Setup Complete
                  </Typography>
                  <Typography variant='subtitle1'>
                    {provider.data.setupComplete ? 'Yes' : 'No'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    System Default Provider
                  </Typography>
                  <Typography variant='subtitle1'>
                    {provider.data.systemDefaultProvider ? 'Yes' : 'No'}
                  </Typography>
                </div>
                <div>
                  <Typography color='text.secondary' variant='overline'>
                    Provider ID
                  </Typography>
                  <Stack alignItems='center' direction='row' spacing={0.5}>
                    <Typography
                      sx={{ fontFamily: 'monospace' }}
                      variant='body2'>
                      {managedAccountProviderId.slice(0, 8)}…
                    </Typography>
                    <CopyableText value={managedAccountProviderId} />
                  </Stack>
                </div>
              </Stack>

              <div>
                <Typography gutterBottom variant='subtitle2'>
                  Provider Display
                </Typography>
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Display Name
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.providerDisplayName || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Phone
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.providerDisplayPhoneNumber || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Email
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.providerDisplayEmail || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Logo URI
                    </Typography>
                    <Typography
                      sx={{ overflowWrap: 'anywhere' }}
                      variant='subtitle1'>
                      {provider.data.providerLogoUri || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      ADV Link
                    </Typography>
                    <Typography
                      sx={{ overflowWrap: 'anywhere' }}
                      variant='subtitle1'>
                      {provider.data.providerAdvLink || '-'}
                    </Typography>
                  </div>
                </Stack>
              </div>

              <div>
                <Typography gutterBottom variant='subtitle2'>
                  Dynamic QDIA
                </Typography>
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Permits Dynamic QDIA
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.permitsDynamicQDIA ? 'Yes' : 'No'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Dynamic QDIA Age Default
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.dynamicQDIAAgeDefault ?? '-'}
                    </Typography>
                  </div>
                </Stack>
              </div>

              <div>
                <Typography gutterBottom variant='subtitle2'>
                  Index
                </Typography>
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Index Name
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.indexName || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Index Location
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.indexLocation || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Index Lookback Year
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.indexLookbackYear ?? '-'}
                    </Typography>
                  </div>
                </Stack>
              </div>

              <div>
                <Typography gutterBottom variant='subtitle2'>
                  Fees & Disclosures
                </Typography>
                <Stack spacing={2}>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Fees
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }} variant='body2'>
                      {provider.data.fees || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Fee Disclosure Message
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }} variant='body2'>
                      {provider.data.feeDisclosureMessage || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Additional Disclosure
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }} variant='body2'>
                      {provider.data.providerAdditionalDisclosure || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      CONNECT Disclosures
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap' }} variant='body2'>
                      {provider.data.disclosureStorage || '-'}
                    </Typography>
                  </div>
                </Stack>
              </div>

              <div>
                <Typography gutterBottom variant='subtitle2'>
                  Advanced
                </Typography>
                <Stack
                  direction='row'
                  spacing={3}
                  sx={{ flexWrap: 'wrap', gap: 2 }}>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Model Key
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.modelKey || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      White Label ID
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.whiteLabelId || '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Created At
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.createdAt
                        ? new Date(provider.data.createdAt).toLocaleString()
                        : '-'}
                    </Typography>
                  </div>
                  <div>
                    <Typography color='text.secondary' variant='overline'>
                      Updated At
                    </Typography>
                    <Typography variant='subtitle1'>
                      {provider.data.updatedAt
                        ? new Date(provider.data.updatedAt).toLocaleString()
                        : '-'}
                    </Typography>
                  </div>
                </Stack>
              </div>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={1}>
        <Typography variant='h6'>Linked Advisory Organizations</Typography>
        <DataGrid
          columns={advisoryOrgColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.advisoryOrganizationId}
          loading={advisoryOrgs.isFetching}
          rows={advisoryOrgs.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'linkedAdvisoryOrganizations'
            }
          }}
        />
      </Stack>

      <Stack spacing={1}>
        <Typography variant='h6'>Portfolio Funds</Typography>
        <DataGrid
          columns={portfolioColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.managedAccountPortfolioId}
          initialState={{
            pagination: { paginationModel: { page: 0, pageSize: 25 } }
          }}
          loading={portfolios.isFetching}
          pageSizeOptions={[10, 25, 50, 100]}
          rows={portfolios.data ?? []}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'portfolioFunds'
            }
          }}
        />
      </Stack>

      <Dialog
        data-testid='portfolioDetailDialog'
        isOpen={!!viewingPortfolio}
        onClose={onCloseViewingPortfolio}>
        <DialogTitle variant='h3'>Portfolio Fund Details</DialogTitle>
        <DialogContent>
          {viewingPortfolio && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Fund ID
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.fundId}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  CUSIP
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.cusip || '-'}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  ISIN
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.isin || '-'}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Volatility Description
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.volatilityDescription}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Investor Profile Description
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.investorProfileDescription}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Targeted Rate of Return
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.targetedRateOfReturn ?? '-'}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Rate of Return Probability
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.rateOfReturnProbability}
                </Typography>
              </div>
              <div>
                <Typography color='text.secondary' variant='overline'>
                  Worst Case Gain/Loss
                </Typography>
                <Typography variant='body1'>
                  {viewingPortfolio.worstCaseGainLoss}
                </Typography>
              </div>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export const Route = createFileRoute(
  '/managed-account-providers/$managedAccountProviderId'
)({
  component: ManagedAccountProviderDetailPage
});
